class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.closingTimeout;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.openHandler = this.open.bind(this);
    this.closeWithClosingClassHandler = this.closeWithClosingClass.bind(this);

    this.closeButton = this.querySelector('.disclosure__close-button');
    if(this.closeButton) {
      this.onClickCloseButtonHandler = this.onClickCloseButton.bind(this);
      this.closeButton.addEventListener('click', this.onClickCloseButtonHandler);
    }
    
    if(!this.dataset.ignoreHover) {
      this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;

      this.debouncedOnChange = debounce((event) => {
        this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;
        this.initHoverDetails();
      }, 300);

      this.debouncedOnChangeHandler = this.debouncedOnChange.bind(this);
  
      window.addEventListener('resize', this.debouncedOnChangeHandler);
  
      this.initHoverDetails();
    }
    
    this.initClickSummary();
  }

  disconnectedCallback() {
    if(this.isAbleToUseMouse) {
      Shopify.removeEventListener(this.mainDetailsToggle, 'mouseenter', this.openHandler);
      Shopify.removeEventListener(this.mainDetailsToggle, 'mouseleave', this.closeWithClosingClassHandler);
    }

    if(this.closeButton) {
      this.closeButton.removeEventListener('click', this.onClickCloseButtonHandler);
    }

    if(this.debouncedOnChangeHandler) {
      window.removeEventListener('resize', this.debouncedOnChangeHandler);
    }
  }

  initHoverDetails () {
    if(this.isAbleToUseMouse) {
      Shopify.addListener(this.mainDetailsToggle, 'mouseenter', this.openHandler);
      Shopify.addListener(this.mainDetailsToggle, 'mouseleave', this.closeWithClosingClassHandler);
    } else {
      this.mainDetailsToggle.removeEventListener('mouseenter', this.openHandler);
      this.mainDetailsToggle.removeEventListener('mouseleave', this.closeWithClosingClassHandler);
    }
  }

  initClickSummary() {
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', (e) => {
      e.preventDefault();
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.closeWithClosingClass();
      } else {
        this.open();
      }
    });
  }

  closeWithClosingClass() {
    if(this.classList.contains('ignore-close')) return;
    this.mainDetailsToggle.classList.add('closing');
    if(this.closingTimeout) {
      clearTimeout(this.closingTimeout);
    }
    this.closingTimeout = setTimeout(() => {
      this.close();
      this.mainDetailsToggle.classList.remove('closing');
    }, 300);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.closeWithClosingClass();
    this.mainDetailsToggle.querySelector('summary').focus();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeWithClosingClass();
    })
  }

  onClickCloseButton() {
    this.closeWithClosingClass();
  }

  playAnimation() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  open() {
    this.mainDetailsToggle.setAttribute('open', true);
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    this.playAnimation();
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

let hoverSupport = matchMedia('(hover: hover)').matches;
window.addEventListener('resize', () => {
  hoverSupport = matchMedia('(hover: hover)').matches;
});

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header');
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.querySelectorAll(':scope > details > .header__menu-item--link').forEach(element => element.addEventListener('click', this.onClickSummary.bind(this)));
    this.stickyHeader = this.closest('sticky-header');
  }

  initHoverDetails () {
    if (!this.mainDetailsToggle) return;
    this.summary = this.summary || this.mainDetailsToggle.querySelector('summary');
    if (!this.summary) return;

    if (this.isAbleToUseMouse) {
      if (!this.onSummaryEnterHandler) {
        this.onSummaryEnterHandler = this.onSummaryEnter.bind(this);
        this.onSummaryLeaveHandler = this.onSummaryLeave.bind(this);
        this.onContentEnterHandler = this.onContentEnter.bind(this);
        this.onContentLeaveHandler = this.onContentLeave.bind(this);
      }

      Shopify.addListener(this.summary, 'mouseenter', this.onSummaryEnterHandler);
      Shopify.addListener(this.summary, 'mouseleave', this.onSummaryLeaveHandler);
      Shopify.addListener(this.content, 'mouseenter', this.onContentEnterHandler);
      Shopify.addListener(this.content, 'mouseleave', this.onContentLeaveHandler);
    } else {
      this.summary.removeEventListener('mouseenter', this.onSummaryEnterHandler);
      this.summary.removeEventListener('mouseleave', this.onSummaryLeaveHandler);
      this.content.removeEventListener('mouseenter', this.onContentEnterHandler);
      this.content.removeEventListener('mouseleave', this.onContentLeaveHandler);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.summary && this.onSummaryEnterHandler) {
      this.summary.removeEventListener('mouseenter', this.onSummaryEnterHandler);
      this.summary.removeEventListener('mouseleave', this.onSummaryLeaveHandler);
    }
    if (this.content && this.onContentEnterHandler) {
      this.content.removeEventListener('mouseenter', this.onContentEnterHandler);
      this.content.removeEventListener('mouseleave', this.onContentLeaveHandler);
    }
  }

  getHoverState() {
    if (!window.__headerMenuHoverState) {
      window.__headerMenuHoverState = { lockUntil: 0, activeId: null };
    }
    return window.__headerMenuHoverState;
  }

  onSummaryEnter() {
    const hoverState = this.getHoverState();
    const now = Date.now();
    this.isHoveringSummary = true;
    if (this.openTimeout) clearTimeout(this.openTimeout);
    if (this.closeTimeout) clearTimeout(this.closeTimeout);

    if (now < hoverState.lockUntil && hoverState.activeId && hoverState.activeId !== this.mainDetailsToggle.id) {
      const remaining = hoverState.lockUntil - now;
      this.openTimeout = setTimeout(() => {
        if (!this.isHoveringSummary) return;
        this.open();
      }, remaining + 20);
      return;
    }

    this.openTimeout = setTimeout(() => {
      if (!this.isHoveringSummary) return;
      this.open();
    }, 120);
  }

  onSummaryLeave() {
    const hoverState = this.getHoverState();
    hoverState.lockUntil = Date.now() + 250;
    hoverState.activeId = this.mainDetailsToggle.id;
    this.isHoveringSummary = false;

    if (this.openTimeout) clearTimeout(this.openTimeout);
    if (this.closeTimeout) clearTimeout(this.closeTimeout);

    this.closeTimeout = setTimeout(() => {
      if (!this.isHoveringContent) this.closeWithClosingClass();
    }, 200);
  }

  onContentEnter() {
    this.isHoveringContent = true;
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
  }

  onContentLeave() {
    this.isHoveringContent = false;
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      this.closeWithClosingClass();
    }, 200);
  }

  onClickSummary(event) {
    if(hoverSupport && !this.dataset.ignoreHover) {
      event.preventDefault();
      window.location = event.currentTarget.dataset.href;
    }
  }

  open() {
    super.open();
    if(this.stickyHeader) {
      this.stickyHeader.setPreventOpen(true);
      this.stickyHeader.setPreventHide(true);
    }
  }

  close() {
    super.close();
    if(this.stickyHeader) {
      this.stickyHeader.setPreventOpen(false);
      this.stickyHeader.setPreventHide(false);
    }
  }

  onToggle() { 
    if (!this.header) return;
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(this.header.getBoundingClientRect().bottom)}px`);
    if(this.mainDetailsToggle.hasAttribute('open')) {
      if (this.content && this.content.classList.contains('header__submenu')) {
        const rect = this.summary.getBoundingClientRect();
        const initialLeft = Math.floor(rect.left);
        this.content.style.setProperty('--submenu-left', `${initialLeft}px`);

        requestAnimationFrame(() => {
          const contentRect = this.content.getBoundingClientRect();
          const viewportWidth = document.documentElement.clientWidth;
          let left = initialLeft;
          left = Math.min(left, viewportWidth - contentRect.width - 16);
          left = Math.max(16, left);
          this.content.style.setProperty('--submenu-left', `${Math.floor(left)}px`);
        });
      }
      if(this.content.getBoundingClientRect().bottom > document.documentElement.clientHeight - 50) {
        this.content.classList.add('outside-viewport');
        document.body.classList.add('overflow-hidden');
        this.content.style.setProperty(
          "--top-position",
          `${this.content.getBoundingClientRect().top}px`
        );
      }
    } else {
      this.content.classList.remove('outside-viewport');
      document.body.classList.remove('overflow-hidden');
      this.content.style.removeProperty("--top-position");
      if (this.content) this.content.style.removeProperty('--submenu-left');
    }
  }
}

customElements.define('header-menu', HeaderMenu);
